import { z } from "zod/v4-mini";

import { TelemonitoringTags, telemonitoringTagsSchema } from "@/models/CarePlanModel.ts";
import {
  ConditionType,
  MedicationSchema,
  conditionTypeSchema,
  medicationSchemeSchema,
} from "@/models/ConditionModel.ts";
import type { PaletteColors } from "@/theme/PaletteOverrides.ts";
import { Schema, cz } from "@/utils/zodUtils.ts";

import { UnreadMessageCount } from "./MessagingModel";
import { RenewType, renewTypeSchema } from "./PatientRenewalInfoModel";

export type PatientLastSeen = "olderThanAMonth" | "olderThanTwoWeeks" | "recent" | "never" | "unknown";

export type PatientListItemPractitioner = {
  id: string;
  givenName: string;
  familyName: string;
};

export type PatientListItem = {
  id: string;
  givenName: string;
  familyName: string;

  doctor?: PatientListItemPractitioner;
  telemonitoringNurse?: PatientListItemPractitioner;
  unreadMessages: UnreadMessageCount;
  diabetesType: ConditionType;
  insulinTreatmentType: MedicationSchema;
  renewalSuggestion?: RenewType;
  hasNurseNotice: boolean;
  hasPendingReinclusionSuggestion: boolean;

  tags: TelemonitoringTags[];

  patientLastSeen: PatientLastSeen;
};

export type PatientList = PatientListItem[];

export type PatientListByCategory = {
  monitored: PatientList;
  pregnant: PatientList;
  all: PatientList;
};

const patientListItemDoctorSchema: Schema<PatientListItemPractitioner> = cz.transform(
  z.object({
    id: z.string(),
    firstname: z.string(),
    lastname: z.string(),
  }),
  data => ({
    id: data.id,
    givenName: data.firstname,
    familyName: data.lastname,
  }),
);

export const patientListItemSchema: Schema<PatientListItem> = cz.transform(
  z.object({
    patient_id: z.string(),
    firstname: z.string(),
    lastname: z.string(),
    doctor: cz.optional(patientListItemDoctorSchema),
    telemonitoring_nurse: cz.optional(patientListItemDoctorSchema),
    unread_messages_from_practitioner: z.number(),
    unread_messages_from_patient: z.number(),
    renewal_suggestion: cz.optional(renewTypeSchema),
    has_nurse_notice: z.boolean(),
    has_pending_reinclusion_suggestion: cz.optional(z.boolean()),
    diabetes_type: conditionTypeSchema,
    medication_schema: medicationSchemeSchema,
    telemonitoring_tags: cz.forcedArray(telemonitoringTagsSchema),
    patient_last_seen: z.literal(["olderThanAMonth", "olderThanTwoWeeks", "recent", "never", "unknown"]),
  }),
  data => ({
    id: data.patient_id,
    givenName: data.firstname,
    familyName: data.lastname,
    doctor: data.doctor,
    telemonitoringNurse: data.telemonitoring_nurse,
    unreadMessages: {
      fromPractitioner: data.unread_messages_from_practitioner,
      fromPatient: data.unread_messages_from_patient,
    },
    diabetesType: data.diabetes_type,
    insulinTreatmentType: data.medication_schema,
    renewalSuggestion: data.renewal_suggestion,
    hasNurseNotice: data.has_nurse_notice,
    hasPendingReinclusionSuggestion: data.has_pending_reinclusion_suggestion ?? false,
    tags: data.telemonitoring_tags,
    patientLastSeen: data.patient_last_seen,
  }),
);

export const patientListByCategorySchema: Schema<PatientListByCategory> = z.object({
  monitored: cz.forcedArray(patientListItemSchema),
  pregnant: cz.forcedArray(patientListItemSchema),
  all: cz.forcedArray(patientListItemSchema),
});

export const patientListItemNeedsAttention = (patient: PatientListItem): boolean =>
  patient.unreadMessages.fromPatient + patient.unreadMessages.fromPractitioner > 0 || patient.hasNurseNotice;

export const telemonitoringTagColorMapping = (tag: TelemonitoringTags): PaletteColors => {
  switch (tag) {
    case "active":
    case "gestational_no_insulin":
      return "green";
    case "expiring_soon":
      return "neutral";
    case "expired":
    case "expired_recently":
      return "red";
    case "no_glycemia_data":
      return "purple";
    case "nurse_asked_for_reinclusion":
      return "blue";
  }
};

export const patientLastSeenTranslationMapping = (bucket: PatientLastSeen | "never"): string => {
  switch (bucket) {
    case "recent":
      return "pages.patients.lastSeen.recent";
    case "olderThanTwoWeeks":
      return "pages.patients.lastSeen.olderThanTwoWeeks";
    case "olderThanAMonth":
      return "pages.patients.lastSeen.olderThanMonth";
    case "unknown":
      return "pages.patients.lastSeen.unknown";
    case "never":
      return "pages.patients.lastSeen.never";
  }
};

export const patientLastSeenColor = (bucket: PatientLastSeen | "never"): PaletteColors => {
  switch (bucket) {
    case "recent":
      return "green";
    case "olderThanTwoWeeks":
      return "neutral";
    case "olderThanAMonth":
      return "red";
    case "never":
      return "purple";
    case "unknown":
      return "violet";
  }
};

export const telemonitoringTagTranslationMapping = (tag: TelemonitoringTags): string => {
  switch (tag) {
    case "active":
      return "models.telemonitoringTag.active";
    case "expiring_soon":
      return "models.telemonitoringTag.expiringSoon";
    case "expired":
      return "models.telemonitoringTag.expired";
    case "expired_recently":
      return "models.telemonitoringTag.expiredRecently";
    case "gestational_no_insulin":
      return "models.telemonitoringTag.gestationalNoInsulin";
    case "no_glycemia_data":
      return "models.telemonitoringTag.onboarding";
    case "nurse_asked_for_reinclusion":
      return "models.telemonitoringTag.nurseAskedForReinclusion";
  }
};
